import React, { useState, useEffect } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../stili/AggiornaGiocatoreLog.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function AggGiocatore() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [giocatori, setGiocatori] = useState([]);
  const [giocatoreInModifica, setGiocatoreInModifica] = useState(null);
  const [formEdit, setFormEdit] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadFigli = async () => {
      setLoading(true);

      try {
        const res = await authFetch(`${API_URL}/api/genitore/figli-loggato`);

        if (!res.ok) {
          throw new Error('Errore nel caricamento dei giocatori');
        }

        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data : [];

        const listaNormalizzata = lista.map((g) => ({
          ...g,
          idGiocatore: g.idGiocatore ?? g.id,
          categoriaVisualizzata:
            g.categoria ||
            g.nomeCategoria ||
            g.categoriaNome ||
            '-',
          stagioneVisualizzata:
            g.stagione ||
            g.nomeStagione ||
            '-'
        }));

        if (mounted) {
          setGiocatori(listaNormalizzata);
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          toast.error(err.message || 'Errore fetch giocatori');
          setGiocatori([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadFigli();

    return () => {
      mounted = false;
    };
  }, [authFetch]);

  function handleEditClick(g) {
    setGiocatoreInModifica(g.idGiocatore ?? g.id);
    setFormEdit({
      ...g,
      idGiocatore: g.idGiocatore ?? g.id,
      nome: (g.nome || '').toUpperCase(),
      cognome: (g.cognome || '').toUpperCase(),
      codiceFiscale: (g.codiceFiscale || '').toUpperCase(),
      luogoNascita: (g.luogoNascita || '').toUpperCase(),
      numeroTesserino: g.numeroTesserino || '',
      noteSanitarie: g.noteSanitarie || ''
    });
  }

  function handleCancel(g) {
    setFormEdit({
      ...g,
      idGiocatore: g.idGiocatore ?? g.id,
      nome: (g.nome || '').toUpperCase(),
      cognome: (g.cognome || '').toUpperCase(),
      codiceFiscale: (g.codiceFiscale || '').toUpperCase(),
      luogoNascita: (g.luogoNascita || '').toUpperCase(),
      numeroTesserino: g.numeroTesserino || '',
      noteSanitarie: g.noteSanitarie || ''
    });
    setGiocatoreInModifica(null);
  }

  function handleEditChange(e) {
    const { name, value } = e.target;

    const campiUppercase = [
      'nome',
      'cognome',
      'codiceFiscale',
      'luogoNascita'
    ];

    if (name === 'codiceFiscale') {
      setFormEdit((prev) => ({
        ...prev,
        [name]: value.replace(/\s+/g, '').toUpperCase()
      }));
      return;
    }

    setFormEdit((prev) => ({
      ...prev,
      [name]: campiUppercase.includes(name) ? value.toUpperCase() : value
    }));
  }

  async function handleSave(idGiocatore) {
    if (!idGiocatore) {
      toast.error('Id giocatore non valido');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        nome: formEdit.nome?.trim().toUpperCase() || '',
        cognome: formEdit.cognome?.trim().toUpperCase() || '',
        codiceFiscale: formEdit.codiceFiscale?.trim().toUpperCase() || '',
        dataNascita: formEdit.dataNascita || null,
        luogoNascita: formEdit.luogoNascita?.trim().toUpperCase() || '',
        numeroTesserino: formEdit.numeroTesserino?.trim() || '',
        noteSanitarie: formEdit.noteSanitarie || ''
      };

      const res = await authFetch(`${API_URL}/api/genitore/update-giocatore/${idGiocatore}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Errore nel salvataggio');
      }

      const aggiornato = await res.json().catch(() => null);

      if (!aggiornato) {
        throw new Error('Risposta non valida dal server');
      }

      const aggiornatoNormalizzato = {
        ...aggiornato,
        idGiocatore: aggiornato.idGiocatore ?? aggiornato.id,
        categoriaVisualizzata:
          aggiornato.categoria ||
          aggiornato.nomeCategoria ||
          aggiornato.categoriaNome ||
          formEdit.categoriaVisualizzata ||
          formEdit.categoria ||
          formEdit.nomeCategoria ||
          '-',
        stagioneVisualizzata:
          aggiornato.stagione ||
          aggiornato.nomeStagione ||
          formEdit.stagioneVisualizzata ||
          formEdit.stagione ||
          formEdit.nomeStagione ||
          '-'
      };

      setGiocatori((prev) =>
        prev.map((g) =>
          (g.idGiocatore ?? g.id) === idGiocatore ? aggiornatoNormalizzato : g
        )
      );

      setGiocatoreInModifica(null);
      toast.success('Dati giocatore aggiornati correttamente');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Errore di connessione');
    } finally {
      setSaving(false);
    }
  }

  function formattaDataItaliana(data) {
    if (!data) return '-';
    const d = new Date(data);
    if (isNaN(d)) return data;
    const giorno = String(d.getDate()).padStart(2, '0');
    const mese = String(d.getMonth() + 1).padStart(2, '0');
    const anno = d.getFullYear();
    return `${giorno}/${mese}/${anno}`;
  }

  if (loading) {
    return <p className="agglog-loading">Caricamento...</p>;
  }

  return (
    <div className="agg-container-log" translate="no">
      <h2 className="agg-log-title">Aggiorna Dati Giocatori</h2>

      {giocatori.length === 0 ? (
        <div className="agglog-empty-box">
          Nessun giocatore associato al genitore loggato.
        </div>
      ) : (
        giocatori.map((g, index) => {
          const idCorrente = g.idGiocatore ?? g.id;
          const itemKey =
            idCorrente ??
            `${g.codiceFiscale || 'cf'}-${g.cognome || 'cognome'}-${g.nome || 'nome'}-${index}`;

          return (
            <div key={itemKey} className="agg-item-wrapper-log">
              {giocatoreInModifica !== idCorrente && (
                <div className="agg-card-log">
                  <div className="agg-card-header-log">
                    {g.nome} {g.cognome}
                  </div>

                  <div className="agg-card-grid-log">
                    <div className="agg-card-field-log">
                      <label>Codice Fiscale</label>
                      <span>{g.codiceFiscale || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Data Nascita</label>
                      <span>{formattaDataItaliana(g.dataNascita)}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Luogo Nascita</label>
                      <span>{g.luogoNascita || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Numero Tesserino</label>
                      <span>{g.numeroTesserino || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Note Sanitarie</label>
                      <span>{g.noteSanitarie || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Tipologia Iscrizione</label>
                      <span>{g.statoPagamento || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Categoria</label>
                      <span>{g.categoriaVisualizzata || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Stagione Sportiva</label>
                      <span>{g.stagioneVisualizzata || '-'}</span>
                    </div>

                    <div className="agg-card-field-log">
                      <label>Stato</label>
                      <span>{g.stato || '-'}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="agg-modifica-btn-log"
                    onClick={() => handleEditClick(g)}
                  >
                    Modifica
                  </button>
                </div>
              )}

              {giocatoreInModifica === idCorrente && (
                <div className="agg-espansione-log">
                  <div className="agg-espansione-inner-log">
                    <div className="agg-campo-log">
                      <label>Nome</label>
                      <input
                        className="uppercase"
                        name="nome"
                        value={formEdit.nome || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Cognome</label>
                      <input
                        className="uppercase"
                        name="cognome"
                        value={formEdit.cognome || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Codice Fiscale</label>
                      <input
                        className="uppercase"
                        name="codiceFiscale"
                        value={formEdit.codiceFiscale || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Data Nascita</label>
                      <input
                        type="date"
                        name="dataNascita"
                        value={formEdit.dataNascita || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Luogo Nascita</label>
                      <input
                        className="uppercase"
                        name="luogoNascita"
                        value={formEdit.luogoNascita || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Numero Tesserino</label>
                      <input
                        name="numeroTesserino"
                        value={formEdit.numeroTesserino || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Note Sanitarie</label>
                      <input
                        name="noteSanitarie"
                        value={formEdit.noteSanitarie || ''}
                        onChange={handleEditChange}
                        disabled={saving}
                      />
                    </div>

                    <div className="agg-campo-log">
                      <label>Tipologia Iscrizione</label>
                      <input value={g.statoPagamento || ''} readOnly />
                    </div>

                    <div className="agg-campo-log">
                      <label>Categoria</label>
                      <input value={g.categoriaVisualizzata || ''} readOnly />
                    </div>

                    <div className="agg-campo-log">
                      <label>Stagione</label>
                      <input value={g.stagioneVisualizzata || ''} readOnly />
                    </div>

                    <div className="agg-campo-log">
                      <label>Stato</label>
                      <input value={g.stato || ''} readOnly />
                    </div>
                  </div>

                  <div className="agg-col-bottoni-log">
                    <button
                      type="button"
                      className="agg-salva-btn-log"
                      onClick={() => handleSave(idCorrente)}
                      disabled={saving}
                    >
                      {saving ? 'Salvataggio...' : 'Salva'}
                    </button>

                    <button
                      type="button"
                      className="agg-annulla-btn-log"
                      onClick={() => handleCancel(g)}
                      disabled={saving}
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      <div className="agglog-footer">
        <button
          type="button"
          className="agglog-torna"
          onClick={() => navigate('/gestionale2')}
          disabled={saving}
        >
          Torna al gestionale
        </button>
      </div>
    </div>
  );
}